import React, { FC } from "react";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { LuReply } from "react-icons/lu";

import { AiWriteAction } from "../../../slices/AiSlice";
import {
  ComposeTone,
  IMPROVE_ACTIONS,
  NEW_EMAIL_PROMPTS,
  REPLY_PROMPTS,
  TONES,
} from "./composeEmail.utils";
import type { ComposeAiContext } from "./ComposeEmailWindow";
import { SparkleBadge, SparkleIcon } from "./SparkleIcon";

type Props = {
  isAnswer: boolean;
  replyingTo?: string;
  aiContext?: ComposeAiContext;
  prompt: string;
  setPrompt: (value: string) => void;
  tone: ComposeTone;
  setTone: (value: ComposeTone) => void;
  busy: boolean;
  bodyEmpty: boolean;
  onGenerate: (prompt?: string) => void;
  onReadyReply: (text: string, label: string) => void;
  onImprove: (action: AiWriteAction) => void;
};

const IMPROVE_ICONS: Record<string, string> = {
  improve: "✦",
  fix: "✓",
  shorten: "↘",
  formal: "◆",
  friendlier: "☺",
};

/** Right column of the compose window: draft from a description, or improve the text. */
export const ComposeAiPanel: FC<Props> = ({
  isAnswer,
  replyingTo,
  aiContext,
  prompt,
  setPrompt,
  tone,
  setTone,
  busy,
  bodyEmpty,
  onGenerate,
  onReadyReply,
  onImprove,
}) => {
  const ready = aiContext?.replies?.filter((r) => r.text) || [];
  const canGenerate = !!prompt.trim() && !busy;
  const first = (replyingTo || "").split(/[\s@]/)[0];

  return (
    <div
      className={
        "flex min-h-0 w-[340px] flex-none flex-col border-l border-[#ECE7FF]"
      }
      style={{ background: "linear-gradient(180deg,#F8F6FF 0%,#FCFBFF 100%)" }}>
      <div
        className={
          "flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-[18px] pb-4 pt-[18px]"
        }>
        {isAnswer && aiContext?.summary && (
          <div
            className={
              "rounded-[11px] border border-[#E9E4FF] bg-white px-[13px] py-3"
            }>
            <div
              className={
                "mb-[6px] flex items-center gap-[7px] text-[11.5px] font-extrabold uppercase tracking-[0.4px] text-[#9A90C8]"
              }>
              <LuReply size={13} />
              Replying to {first || "them"}
            </div>
            <div className={"text-[13px] leading-[1.55] text-[#3A4256]"}>
              {aiContext.summary}
            </div>
          </div>
        )}

        <div className={"flex flex-col gap-[10px]"}>
          <div className={"flex items-center gap-2"}>
            <SparkleBadge size={26} />
            <span className={"text-[14.5px] font-extrabold text-[#2A1F66]"}>
              {isAnswer ? "Draft a reply" : "Write with AI"}
            </span>
          </div>
          <div
            className={
              "rounded-[11px] border-[1.5px] bg-white transition-colors"
            }
            style={{ borderColor: prompt.trim() ? "#B9A9FF" : "#E4DCFF" }}>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (canGenerate) onGenerate();
                }
              }}
              placeholder={
                isAnswer
                  ? `What do you want to say? e.g. "Thank ${
                      first || "them"
                    } and confirm the next steps"`
                  : 'Describe the email, e.g. "Ask Priya for the August equipment invoices and remind her the GST return is due soon"'
              }
              className={
                "block h-[84px] w-full resize-none rounded-[11px] border-none bg-transparent px-3 pb-1 pt-[11px] text-[13.5px] leading-[1.5] text-[#1F2433] outline-none placeholder:text-[#A3A9B8]"
              }
            />
            <div
              className={
                "flex items-center gap-[6px] pb-2 pl-[10px] pr-2 pt-[6px]"
              }>
              <span className={"flex-1 text-[11.5px] text-[#A3A9B8]"}>
                Enter to generate
              </span>
              <button
                type={"button"}
                disabled={!canGenerate}
                onClick={() => onGenerate()}
                className={
                  "inline-flex h-[30px] items-center gap-[6px] rounded-[8px] px-3 text-[12.5px] font-bold text-white"
                }
                style={{
                  background: canGenerate
                    ? "linear-gradient(135deg,#6B4EFF,#9152FF)"
                    : "#C9BFF5",
                  cursor: canGenerate ? "pointer" : "not-allowed",
                }}>
                <SparkleIcon size={13} />
                Generate
              </button>
            </div>
          </div>

          <div className={"flex flex-wrap gap-[6px]"}>
            {ready.length > 0
              ? ready.map((r) => (
                  <button
                    type={"button"}
                    key={r.label}
                    disabled={busy}
                    onClick={() => onReadyReply(r.text, "Drafted with AI")}
                    className={
                      "inline-flex h-7 items-center rounded-[14px] border border-[#E4DCFF] bg-white px-[11px] text-[12.5px] font-semibold text-[#4A36B8] hover:border-[#6B4EFF] hover:bg-[#F4F0FF] disabled:opacity-60"
                    }>
                    {r.label}
                  </button>
                ))
              : (isAnswer ? REPLY_PROMPTS : NEW_EMAIL_PROMPTS).map((p) => (
                  <button
                    type={"button"}
                    key={p}
                    disabled={busy}
                    onClick={() => {
                      setPrompt(p);
                      onGenerate(p);
                    }}
                    className={
                      "inline-flex h-7 items-center rounded-[14px] border border-[#E4DCFF] bg-white px-[11px] text-[12.5px] font-semibold text-[#4A36B8] hover:border-[#6B4EFF] hover:bg-[#F4F0FF] disabled:opacity-60"
                    }>
                    {p}
                  </button>
                ))}
          </div>

          <div className={"mt-[2px] flex items-center gap-[10px]"}>
            <span className={"w-10 text-[12.5px] font-bold text-[#6B7185]"}>
              Tone
            </span>
            <span
              className={
                "flex flex-1 gap-[3px] rounded-[9px] bg-[#EFEBFF] p-[3px]"
              }>
              {TONES.map((t) => (
                <button
                  type={"button"}
                  key={t.value}
                  onClick={() => setTone(t.value)}
                  className={
                    "inline-flex h-[26px] flex-1 items-center justify-center rounded-[7px] text-[12px] font-bold"
                  }
                  style={
                    tone === t.value
                      ? {
                          background: "#fff",
                          color: "#3A2E7A",
                          boxShadow: "0 1px 3px rgba(42,31,102,0.14)",
                        }
                      : { background: "transparent", color: "#7D7AA8" }
                  }>
                  {t.label}
                </button>
              ))}
            </span>
          </div>
        </div>

        <div className={"h-px bg-[#ECE7FF]"} />

        <div className={"flex flex-col gap-2"}>
          <div className={"flex items-baseline gap-2"}>
            <span className={"text-[14.5px] font-extrabold text-[#2A1F66]"}>
              Improve your draft
            </span>
            {bodyEmpty && (
              <span className={"text-[12px] text-[#A3A9B8]"}>
                Write something first
              </span>
            )}
          </div>
          <div className={"grid grid-cols-2 gap-[7px]"}>
            {IMPROVE_ACTIONS.map((a) => (
              <button
                type={"button"}
                key={a.action}
                disabled={bodyEmpty || busy}
                onClick={() => onImprove(a.action)}
                className={
                  "flex h-[38px] items-center gap-2 rounded-[9px] border border-[#E9E4FF] bg-white px-[11px] text-left text-[13px] font-bold text-[#2A1F66] hover:border-[#B9A9FF] disabled:cursor-not-allowed disabled:text-[#A3A9B8] disabled:opacity-60 disabled:hover:border-[#E9E4FF]"
                }>
                <span className={"w-4 flex-none text-center text-[14px]"}>
                  {IMPROVE_ICONS[a.action]}
                </span>
                <span className={"truncate"}>{a.short}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div
        className={
          "flex flex-none items-start gap-[7px] border-t border-[#ECE7FF] px-[18px] pb-[13px] pt-[11px] text-[11.5px] leading-[1.45] text-[#9A90C8]"
        }>
        <HiOutlineInformationCircle size={14} className={"mt-px flex-none"} />
        The AI only writes drafts. Nothing is sent until you press Send.
      </div>
    </div>
  );
};
